import { Diff, Info } from "lucide-react";
import { useRef, useState, type RefObject } from "react";
import type { IVScoreResponse } from "../../types/market-data";
import type { EconHeaderCountdownEvent } from "../../types/econ-header";
import { FintheonNumericText } from "../numbers/FintheonNumericText";
import {
  LATEST_ECON_PRINT_DRAWER_ID,
  LatestEconPrintDrawer,
} from "./LatestEconPrintDrawer";
import { IVScoreBreakdown } from "./IVScoreBreakdown";
import { urgencyClass, type IvScoreStyle } from "./iv-score-visuals";
import type { IvScorePopupPosition } from "./use-iv-score-tooltip";

export { IVScoreMobileChip } from "./IVScoreMobileChip";

interface LoadingChipProps {
  compactCopy: boolean;
  mobileCombined: boolean;
  latestReleasedEconEvent?: EconHeaderCountdownEvent | null;
}

export function IVScoreLoadingChip(props: LoadingChipProps) {
  const anchorRef = useRef<HTMLElement | null>(null);
  const [latestPrintOpen, setLatestPrintOpen] = useState(false);
  if (props.mobileCombined) {
    if (props.latestReleasedEconEvent) {
      return (
        <>
          <button
            ref={(node) => {
              anchorRef.current = node;
            }}
            type="button"
            className="fintheon-iv-score-card fintheon-black-liquid-glass fintheon-glass-pill mobile-iv-header-glass header-widget-glass solvys-liquid-glass relative flex h-[34px] w-[min(214px,calc(100vw-116px))] min-w-0 items-center justify-center gap-1.5 rounded-full border border-transparent px-2.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-accent)]/55"
            aria-label="Open today's latest released Desk Plan event"
            aria-controls={LATEST_ECON_PRINT_DRAWER_ID}
            aria-expanded={latestPrintOpen}
            onClick={() => setLatestPrintOpen(true)}
          >
            <LoadingValue label="VIX" />
            <span className="mobile-iv-column-divider" aria-hidden="true" />
            <LoadingValue label="IV" prominent />
          </button>
          <LatestEconPrintDrawer
            anchorRef={anchorRef}
            open={latestPrintOpen}
            onClose={() => setLatestPrintOpen(false)}
            latestReleasedEvent={props.latestReleasedEconEvent}
          />
        </>
      );
    }
    return (
      <div className="fintheon-iv-score-card fintheon-black-liquid-glass fintheon-glass-pill mobile-iv-header-glass header-widget-glass solvys-liquid-glass relative flex h-[34px] w-[min(214px,calc(100vw-116px))] min-w-0 items-center justify-center gap-1.5 rounded-full border border-transparent px-2.5">
        <LoadingValue label="VIX" />
        <span className="mobile-iv-column-divider" aria-hidden="true" />
        <LoadingValue label="IV" prominent />
      </div>
    );
  }
  return (
    <>
      <div
        className={`fintheon-iv-score-card fintheon-black-liquid-glass fintheon-glass-pill header-widget-glass solvys-liquid-glass relative flex h-[30px] items-center rounded-[5px] border border-[var(--fintheon-accent)]/20 ${props.compactCopy ? "w-[72px] justify-center" : ""}`}
        data-iv-score-compact={props.compactCopy || undefined}
        data-iv-score-surface="desktop-chip"
      >
        <button
          ref={(node) => {
            anchorRef.current = node;
          }}
          type="button"
          className={`flex h-full min-w-0 items-center gap-1.5 bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-accent)]/55 ${props.compactCopy ? "justify-center px-2" : "px-2.5"}`}
          aria-label="Open latest stored economic print"
          aria-controls={LATEST_ECON_PRINT_DRAWER_ID}
          aria-expanded={latestPrintOpen}
          onClick={() => setLatestPrintOpen(true)}
        >
          <span className="text-[9px] text-gray-500">
            {props.compactCopy ? "IV" : "IV Score"}
          </span>
          <span className="font-mono text-xs font-bold tabular-nums text-gray-600">
            --
          </span>
        </button>
      </div>
      <LatestEconPrintDrawer
        anchorRef={anchorRef}
        open={latestPrintOpen}
        onClose={() => setLatestPrintOpen(false)}
        latestReleasedEvent={props.latestReleasedEconEvent}
      />
    </>
  );
}

interface DesktopChipProps {
  compactCopy: boolean;
  data: IVScoreResponse;
  environment: string;
  marketDetail: string;
  onInfoEnter: () => void;
  onInfoLeave: () => void;
  popupPosition: IvScorePopupPosition | null;
  scoreColor: string;
  showTooltip: boolean;
  style: IvScoreStyle;
  triggerRef: RefObject<HTMLElement | null>;
  latestReleasedEconEvent?: EconHeaderCountdownEvent | null;
}

export function IVScoreDesktopChip(props: DesktopChipProps) {
  const latestPrintAnchorRef = useRef<HTMLElement | null>(null);
  const [latestPrintOpen, setLatestPrintOpen] = useState(false);
  const points = props.data.points;
  return (
    <>
      <div
        className={`fintheon-iv-score-card fintheon-black-liquid-glass fintheon-glass-pill header-widget-glass solvys-liquid-glass relative flex h-[30px] items-center rounded-[5px] border ${props.compactCopy ? "w-[72px] justify-center" : ""}`}
        style={props.style}
        data-iv-score-compact={props.compactCopy || undefined}
        data-iv-score-surface="desktop-chip"
      >
        <button
          ref={(node) => {
            latestPrintAnchorRef.current = node;
          }}
          type="button"
          className={`flex h-full min-w-0 items-center bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-accent)]/55 ${props.compactCopy ? "gap-1.5 pl-2" : "gap-2 pl-2.5"}`}
          aria-label="Open latest stored economic print"
          aria-controls={LATEST_ECON_PRINT_DRAWER_ID}
          aria-expanded={latestPrintOpen}
          onClick={() => setLatestPrintOpen(true)}
        >
          <span className="text-[9px] text-gray-400">IV</span>
          <span
            className="font-mono text-xs font-bold tabular-nums"
            style={{ color: props.scoreColor }}
          >
            <FintheonNumericText
              kind="score"
              text={props.data.score.toFixed(1)}
            />
          </span>
          {props.compactCopy ? null : (
            <span className="text-[9px] font-medium text-[var(--fintheon-text)]/35">
              {props.data.instrument}
            </span>
          )}
          {props.compactCopy ? null : (
            <span
              className="text-[9px] font-medium"
              style={{ color: props.scoreColor }}
            >
              {props.environment}
            </span>
          )}
          {points && !props.compactCopy ? (
            <>
              <span className="text-gray-600 text-[10px]">|</span>
              <Diff className="h-3 w-3 text-[var(--fintheon-accent)]" />
              <span className="text-[10px] font-medium text-[var(--fintheon-accent)]">
                ±<FintheonNumericText text={points.scaledPoints} /> pts
              </span>
              <span
                className={`text-[9px] font-medium ${urgencyClass(points.urgency)}`}
              >
                {points.urgency}
              </span>
            </>
          ) : null}
        </button>
        <div
          ref={(node) => {
            props.triggerRef.current = node;
          }}
          className={`relative ml-0.5 ${props.compactCopy ? "pr-1.5" : "pr-2.5"}`}
          onMouseEnter={props.onInfoEnter}
          onMouseLeave={props.onInfoLeave}
        >
          <button
            type="button"
            aria-label="Open IV score intelligence"
            className="text-gray-500 transition-colors hover:text-gray-400"
            onFocus={props.onInfoEnter}
            onBlur={props.onInfoLeave}
          >
            <Info className={props.compactCopy ? "h-2 w-2" : "h-2.5 w-2.5"} />
          </button>
        </div>
      </div>
      {props.showTooltip && props.popupPosition ? (
        <IVScoreBreakdown
          data={props.data}
          marketDetail={props.marketDetail}
          onMouseEnter={props.onInfoEnter}
          onMouseLeave={props.onInfoLeave}
          position={props.popupPosition}
        />
      ) : null}
      <LatestEconPrintDrawer
        anchorRef={latestPrintAnchorRef}
        open={latestPrintOpen}
        onClose={() => setLatestPrintOpen(false)}
        latestReleasedEvent={props.latestReleasedEconEvent}
      />
    </>
  );
}

function LoadingValue({
  label,
  prominent = false,
}: {
  label: string;
  prominent?: boolean;
}) {
  return (
    <span className="flex min-w-0 items-baseline gap-1">
      <span className="text-[9px] uppercase tracking-[0.16em] text-gray-500">
        {label}
      </span>
      <span
        className={`font-mono text-gray-600 ${prominent ? "text-[13px] font-bold" : "text-[11px]"}`}
      >
        --
      </span>
    </span>
  );
}
